/**
 * Phase 5F — Tutor Learning Plans view.
 * A tutor ONLY sees their own assigned subjects, students, bookings.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, GraduationCap, Clock, BookOpen, FileDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso || '—'; } };

const TutorLearningPlansSection = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await ax.get('/api/me/tutor/learning-plans');
        if (live) setPlans(r.data?.items || []);
      } catch (_e) { if (live) setPlans([]); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  const downloadPdf = async () => {
    try {
      const r = await ax.get('/api/me/tutor/timetable.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'teaching-timetable.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) { /* silent */ }
  };

  if (loading) return null;
  if (plans.length === 0) return null;

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="tutor-learning-plans-section">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">My Teaching Plans</div>
          <h2 className="text-lg font-bold text-gray-900 mt-0.5">Students assigned to you</h2>
          <p className="text-[11px] text-gray-500 mt-0.5">Showing only YOUR subjects · your bookings · your earnings.</p>
        </div>
        <button onClick={downloadPdf}
          className="h-9 px-4 rounded-full border border-gray-200 hover:bg-gray-50 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
          data-testid="tutor-plans-pdf">
          <FileDown className="h-3 w-3" /> Teaching PDF
        </button>
      </div>

      <div className="space-y-3">
        {plans.map(p => {
          const next = p.next_lesson;
          return (
            <div key={p.plan_id} className="border border-gray-200 rounded-xl p-4" data-testid={`tutor-plan-row-${p.plan_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-bold text-sm text-gray-900">{p.student_name || p.student_email}</div>
                  <div className="text-[11px] text-gray-500">{p.student_email}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(p.subjects || []).map(s => (
                      <span key={s.series_id} className="bg-gray-50 border border-gray-200 rounded-full px-2.5 py-0.5 text-[10px] inline-flex items-center gap-1">
                        <BookOpen className="h-2.5 w-2.5 text-red-700" />
                        <strong>{s.subject}</strong>
                        <span className="text-gray-500">· {s.day_of_week} {s.start_time}</span>
                      </span>
                    ))}
                  </div>
                </div>
                {next && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold">Next lesson</div>
                    <div className="text-xs font-bold text-gray-900 flex items-center gap-1 justify-end">
                      <Clock className="h-3 w-3 text-red-700" /> {next.subject}
                    </div>
                    <div className="text-[10px] text-gray-500">{fmt(next.start_time)}</div>
                    {next.join_path && (
                      <a href={next.join_path} target="_blank" rel="noopener noreferrer"
                        className="mt-1 h-7 px-3 rounded-full bg-red-700 hover:bg-red-800 text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                        data-testid={`tutor-next-join-${p.plan_id}`}>
                        <ExternalLink className="h-2.5 w-2.5" /> Open
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-[2px] text-gray-500 font-bold">Subjects</div>
                  <div className="text-base font-bold">{(p.subjects || []).length}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-[2px] text-gray-500 font-bold">Bookings</div>
                  <div className="text-base font-bold">{(p.bookings || []).length}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-[2px] text-gray-500 font-bold">Completed</div>
                  <div className="text-base font-bold">{(p.bookings || []).filter(b => b.status === 'completed').length}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-[2px] text-gray-500 font-bold">Upcoming</div>
                  <div className="text-base font-bold">{(p.bookings || []).filter(b => ['scheduled','approved','confirmed'].includes(b.status)).length}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TutorLearningPlansSection;
